import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getNotifications, updateOnlineStatus, markOfflineBeacon, sendHeartbeat } from '../../services/api';
import ItsmIcon from '../ItsmIcon';
import {
  LayoutDashboard, Ticket, FolderOpen, FolderGit2, Users, Clock, BarChart2,
  BookOpen, Search, Settings, User, Bell, LogOut, Plus,
  ChevronLeft, ChevronRight, Moon, Sun, ChevronDown, Palette,
  CalendarOff, ShieldCheck, CalendarDays, Siren, TrendingUp, Menu,
  CheckCircle2, Trash2,
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   6 COLOR THEMES — Violet is default
   ═══════════════════════════════════════════════ */
const COLOR_THEMES = [
  { key: 'violet',  name: 'Violet',   primary: '#7c3aed', dark: '#6d28d9', light: '#f5f3ff', ring: 'rgba(124,58,237,0.15)', hover: '#f5f3ff' },
  { key: 'indigo',  name: 'Indigo',   primary: '#4f46e5', dark: '#4338ca', light: '#eef2ff', ring: 'rgba(79,70,229,0.15)',  hover: '#eef2ff' },
  { key: 'sky',     name: 'Sky',      primary: '#0284c7', dark: '#0369a1', light: '#e0f2fe', ring: 'rgba(2,132,199,0.15)',  hover: '#e0f2fe' },
  { key: 'emerald', name: 'Emerald',  primary: '#059669', dark: '#047857', light: '#dcfce7', ring: 'rgba(5,150,105,0.15)',  hover: '#dcfce7' },
  { key: 'rose',    name: 'Rose',     primary: '#e11d48', dark: '#be123c', light: '#ffe4e6', ring: 'rgba(225,29,72,0.15)',  hover: '#ffe4e6' },
  { key: 'amber',   name: 'Amber',    primary: '#d97706', dark: '#b45309', light: '#fef3c7', ring: 'rgba(217,119,6,0.15)',  hover: '#fef9c3' },
];

function applyColorTheme(key) {
  const t = COLOR_THEMES.find(c => c.key === key) || COLOR_THEMES[0];
  const r = document.documentElement;
  r.style.setProperty('--primary',       t.primary);
  r.style.setProperty('--primary-dark',  t.dark);
  r.style.setProperty('--primary-light', t.light);
  r.style.setProperty('--primary-50',    t.light);
  r.style.setProperty('--primary-100',   t.light);
  r.style.setProperty('--primary-ring',  t.ring);
  r.style.setProperty('--sb-active-bg',  t.primary);
  r.style.setProperty('--sb-logo-bg',    t.primary);
  /* Table row hover matches theme */
  r.style.setProperty('--primary-hover-row', t.hover);
  localStorage.setItem('itsm_color', key);
}

/* ═══════════════════════════════════════════════
   NAVIGATION
   ═══════════════════════════════════════════════ */
const NAV = {
  admin: [
    { section: 'Main', items: [
      { to: '/dashboard',    Icon: LayoutDashboard, label: 'Dashboard'    },
      { to: '/tickets',      Icon: Ticket,           label: 'All Tickets' },
      { to: '/projects',     Icon: FolderOpen,       label: 'Clients'     },
      { to: '/sub-projects', Icon: FolderGit2,       label: 'Projects'    },
    ]},
    // Deleted Tickets is the Recycle Bin — admin only, since only admins can
    // delete in the first place.
    { section: 'Ticket History', items: [
      { to: '/tickets/closed',  Icon: CheckCircle2, label: 'Closed Tickets'  },
      { to: '/tickets/deleted', Icon: Trash2,       label: 'Deleted Tickets' },
    ]},
    { section: 'Administration', items: [
      { to: '/users',             Icon: Users,        label: 'User Management'  },
      { to: '/checkin',           Icon: Clock,        label: 'Attendance Log'   },
      { to: '/leaves',            Icon: CalendarOff,  label: 'Leave Requests'   },
      { to: '/sla',               Icon: ShieldCheck,  label: 'SLA Dashboard'    },
      { to: '/holidays',          Icon: CalendarDays, label: 'Holiday Calendar' },
      { to: '/escalation-rules',  Icon: Siren,        label: 'Escalation Rules' },
      { to: '/productivity',      Icon: TrendingUp,   label: 'Productivity'     },
      { to: '/reports',           Icon: BarChart2,    label: 'Reports'          },
      { to: '/knowledge-base',    Icon: BookOpen,     label: 'Knowledge Base'   },
      { to: '/audit',             Icon: Search,       label: 'Audit Log'        },
      { to: '/settings',          Icon: Settings,     label: 'Settings'         },
    ]},
    { section: 'Account', items: [
      { to: '/profile', Icon: User, label: 'My Profile' },
    ]},
  ],
  consultant: [
    { section: 'Main', items: [
      { to: '/dashboard',    Icon: LayoutDashboard, label: 'Dashboard'    },
      { to: '/tickets',      Icon: Ticket,           label: 'My Tickets'  },
      { to: '/projects',     Icon: FolderOpen,       label: 'Clients'     },
      { to: '/sub-projects', Icon: FolderGit2,       label: 'Projects'    },
    ]},
    // No Deleted Tickets here: deletion and the Recycle Bin are admin-only.
    { section: 'Ticket History', items: [
      { to: '/tickets/closed', Icon: CheckCircle2, label: 'Closed Tickets' },
    ]},
    { section: 'Work Session', items: [
      { to: '/checkin', Icon: Clock,       label: 'Check-In / Out' },
      { to: '/leaves',  Icon: CalendarOff, label: 'My Leave'       },
      { to: '/sla',     Icon: ShieldCheck, label: 'SLA Dashboard'  },
    ]},
    { section: 'Tools', items: [
      { to: '/reports',        Icon: BarChart2, label: 'Reports'        },
      { to: '/knowledge-base', Icon: BookOpen,  label: 'Knowledge Base' },
    ]},
    { section: 'Account', items: [
      { to: '/profile', Icon: User, label: 'My Profile' },
    ]},
  ],
  user: [
    { section: 'Main', items: [
      { to: '/dashboard',   Icon: LayoutDashboard, label: 'Dashboard'  },
      { to: '/tickets',     Icon: Ticket,           label: 'My Tickets' },
      { to: '/tickets/new', Icon: Plus,             label: 'New Ticket' },
    ]},
    { section: 'Ticket History', items: [
      { to: '/tickets/closed', Icon: CheckCircle2, label: 'Closed Tickets' },
    ]},
    { section: 'Support', items: [
      { to: '/knowledge-base', Icon: BookOpen, label: 'Self Help' },
    ]},
    { section: 'Account', items: [
      { to: '/profile', Icon: User, label: 'My Profile' },
    ]},
  ],
};

function UserAvatar({ name, size = 32, avatarUrl, online }) {
  const ini = name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
  const dot = Math.max(8, Math.round(size * 0.3));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%', overflow: 'hidden',
        background: avatarUrl ? '#fff' : 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.floor(size * 0.36), fontWeight: 700, color: '#fff',
        userSelect: 'none', letterSpacing: '-0.5px',
      }}>
        {avatarUrl
          ? <img src={avatarUrl} alt={name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : ini}
      </div>
      {online !== undefined && (
        <span title={online ? 'Online' : 'Offline'} style={{
          position: 'absolute', bottom: -1, right: -1, width: dot, height: dot,
          borderRadius: '50%', background: online ? '#10b981' : '#9ca3af',
          border: '2px solid var(--bg-card)', boxSizing: 'border-box',
        }} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
export default function AppLayout({ children, pageTitle }) {
  const { user, logoutUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark   = theme === 'dark';

  const [unread,      setUnread]      = useState(0);
  const [dropOpen,    setDropOpen]    = useState(false);
  const [online,      setOnline]      = useState(true);
  const [collapsed,   setCollapsed]   = useState(() => localStorage.getItem('itsm_sidebar') === 'closed');
  const [activeColor, setActiveColor] = useState(() => localStorage.getItem('itsm_color') || 'violet');
  const [isMobile,    setIsMobile]    = useState(() => typeof window !== 'undefined' && window.innerWidth <= 820);
  const [mobileOpen,  setMobileOpen]  = useState(false);

  const dropRef = useRef(null);

  /* Apply saved color theme on mount */
  useEffect(() => { applyColorTheme(activeColor); }, []); // eslint-disable-line

  /* Track viewport so the sidebar becomes an off-canvas drawer on phones/tablets */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 820);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  /* Close the mobile drawer whenever we return to desktop or change route */
  useEffect(() => { if (!isMobile) setMobileOpen(false); }, [isMobile]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]); // eslint-disable-line

  /* Dark mode */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  /* Notifications */
  useEffect(() => {
    getNotifications().then(r => setUnread(r.data.unread_count || 0)).catch(() => {});
  }, []);

  /* Whenever an authenticated session is active (fresh login OR page reopen),
     force the user ONLINE — both locally and on the server. */
  useEffect(() => {
    if (!user) return;
    setOnline(true);
    updateOnlineStatus(true).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  /* Heartbeat: while the page is open, ping every 30s so the server keeps the
     user "online". If the page closes/crashes the pings stop and the server
     auto-expires them to offline — so presence self-heals even if the offline
     beacon below never fires. */
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => { sendHeartbeat().catch(() => {}); }, 30000);
    return () => clearInterval(id);
  }, [user?.id]); // eslint-disable-line

  /* Mark OFFLINE the moment the page is closed or navigated away from.
     pagehide is the most reliable (fires on close/navigation/mobile); the
     beacon uses fetch keepalive so it completes even as the page unloads. */
  useEffect(() => {
    if (!user) return;
    const goOffline = () => markOfflineBeacon();
    window.addEventListener('pagehide', goOffline);
    window.addEventListener('beforeunload', goOffline);
    return () => {
      window.removeEventListener('pagehide', goOffline);
      window.removeEventListener('beforeunload', goOffline);
    };
  }, [user?.id]); // eslint-disable-line

  const toggleOnline = async () => {
    const next = !online;
    setOnline(next);
    try { await updateOnlineStatus(next); } catch (_) { setOnline(!next); }
  };

  /* Close dropdown on outside click */
  useEffect(() => {
    const h = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggleSidebar = () => {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem('itsm_sidebar', next ? 'closed' : 'open');
      return next;
    });
  };

  const handleThemeChange = key => {
    setActiveColor(key);
    applyColorTheme(key);
  };

  const handleLogout = async () => {
    setDropOpen(false);
    const res = await Swal.fire({
      title: 'Sign Out?', text: 'Are you sure?', icon: 'question',
      showCancelButton: true, confirmButtonText: 'Sign Out', cancelButtonText: 'Stay',
      confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280',
      customClass: { popup: 'swal-popup-custom' },
    });
    if (res.isConfirmed) {
      try { await updateOnlineStatus(false); } catch (_) {}
      logoutUser();
      navigate('/login');
    }
  };

  const navGroups    = NAV[user?.role] || NAV.user;
  const currentTheme = COLOR_THEMES.find(t => t.key === activeColor) || COLOR_THEMES[0];
  // On mobile the sidebar is a full-label off-canvas drawer (never the 68px rail).
  const sideCollapsed = isMobile ? false : collapsed;
  const sbW           = sideCollapsed ? '68px' : '256px';

  /* ─── Sidebar CSS injected ─────────────────────────────── */
  const sidebarStyles = `
    .sb-nav-item {
      position: relative;
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 9px 12px;
      border-radius: 9px;
      cursor: pointer;
      color: #aebbd0;
      font-size: 13.5px;
      font-weight: 500;
      text-decoration: none;
      margin-bottom: 3px;
      white-space: nowrap;
      overflow: hidden;
      transition: background 0.18s, color 0.18s, transform 0.18s, box-shadow 0.18s;
      line-height: 1.4;
    }
    .sb-nav-item svg { transition: transform 0.18s; }
    .sb-nav-item:hover {
      background: rgba(255,255,255,0.06);
      color: #f1f5f9;
      transform: translateX(3px);
    }
    .sb-nav-item:hover svg { transform: scale(1.12); }
    .sb-nav-item.sb-active {
      background: linear-gradient(135deg, var(--sb-active-bg), var(--primary-dark, var(--sb-active-bg))) !important;
      color: #ffffff !important;
      font-weight: 600;
      box-shadow: 0 6px 18px var(--primary-ring, rgba(0,0,0,0.35)), inset 0 1px 0 rgba(255,255,255,0.18);
    }
    .sb-nav-item.sb-active:hover {
      color: #ffffff !important;
      transform: translateX(3px);
    }
    .sb-nav-item.sb-active::before {
      content: '';
      position: absolute;
      left: 0; top: 50%;
      transform: translateY(-50%);
      width: 3px; height: 56%;
      border-radius: 0 4px 4px 0;
      background: #ffffff;
      box-shadow: 0 0 10px rgba(255,255,255,0.7);
    }
    .sb-collapse-btn {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
      width: 100%;
      padding: 13px 16px;
      background: #0d1525;
      border: none;
      border-top: 1px solid #1e2a3a;
      color: #60748a;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      font-family: var(--font-main);
      transition: background 0.15s, color 0.15s;
      flex-shrink: 0;
    }
    .sb-collapse-btn:hover { background: #1e2a3a; color: #c4d0e4; }
    .sb-collapse-btn.centered { justify-content: center; }
    @keyframes dropIn {
      from { opacity: 0; transform: translateY(-6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .swal-popup-custom {
      border-radius: 16px !important;
      font-family: var(--font-main) !important;
    }
  `;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg-body)' }}>
      <style>{sidebarStyles}</style>

      {/* Dim backdrop behind the mobile drawer */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 90 }}
        />
      )}

      {/* ════════ SIDEBAR ════════ */}
      <aside style={{
        width: sbW, minWidth: sbW, maxWidth: sbW,
        height: '100vh',
        background: 'linear-gradient(180deg, #141d30 0%, #0e1525 55%, #0b0f1c 100%)',
        display: 'flex', flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease, transform 0.25s ease',
        overflow: 'hidden',
        borderRight: '1px solid #1e2a3a',
        // Desktop: in-flow column. Mobile: fixed off-canvas drawer that slides in.
        ...(isMobile ? {
          position: 'fixed', top: 0, left: 0, zIndex: 100,
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          boxShadow: mobileOpen ? '0 0 40px rgba(0,0,0,0.5)' : 'none',
        } : { zIndex: 50 }),
      }}>

        {/* Logo */}
        <div style={{
          display: 'flex', alignItems: 'center',
          height: '60px',
          padding: '0 14px',
          borderBottom: '1px solid #1e2a3a',
          flexShrink: 0,
          gap: 10,
          overflow: 'hidden',
          justifyContent: sideCollapsed ? 'center' : 'flex-start',
        }}>
          <div style={{ width: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ItsmIcon size={44} idSuffix="-sb" style={{ filter: 'drop-shadow(0 4px 10px rgba(2,40,90,0.45))' }} />
          </div>
          {!sideCollapsed && (
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.2px', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                ITSM Tool
              </div>
              <div style={{ fontSize: 10, color: '#60748a', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600, marginTop: 2 }}>
                ERP Support
              </div>
            </div>
          )}
        </div>

        {/* Nav — scrollable */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 8px' }}>
          {navGroups.map(group => (
            <div key={group.section}>
              {!sideCollapsed ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '16px 10px 5px', userSelect: 'none' }}>
                  <span style={{ width: 12, height: 2, borderRadius: 2, flexShrink: 0, background: `linear-gradient(90deg, ${currentTheme.primary}, transparent)` }} />
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#7286a0', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1 }}>
                    {group.section}
                  </span>
                </div>
              ) : (
                <div style={{ height: 14 }} />
              )}

              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={sideCollapsed ? item.label : undefined}
                  className={({ isActive }) => `sb-nav-item${isActive ? ' sb-active' : ''}`}
                  style={{ justifyContent: sideCollapsed ? 'center' : 'flex-start', padding: sideCollapsed ? '10px 0' : '9px 12px' }}
                >
                  <span style={{ width: 18, height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <item.Icon size={17} strokeWidth={1.9} />
                  </span>
                  {!sideCollapsed && (
                    <>
                      <span style={{ flex: 1 }}>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User info */}
        <div style={{
          padding: '8px 10px',
          borderTop: '1px solid #1e2a3a',
          flexShrink: 0,
          display: 'flex', alignItems: 'center',
          gap: 10,
          overflow: 'hidden',
          justifyContent: sideCollapsed ? 'center' : 'flex-start',
          background: '#0d1525',
        }}>
          <UserAvatar name={user?.name} role={user?.role} size={32} avatarUrl={user?.avatar_url} online={online} />
          {!sideCollapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                  {user?.name}
                </div>
                <div style={{ fontSize: 10.5, color: '#60748a', textTransform: 'capitalize', marginTop: 2 }}>
                  {user?.role}
                </div>
              </div>
              <button
                onClick={handleLogout}
                title="Sign Out"
                style={{
                  background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.25)',
                  color: '#f87171', width: 28, height: 28, borderRadius: 7,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.28)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.12)'; e.currentTarget.style.color = '#f87171'; }}
              >
                <LogOut size={13} />
              </button>
            </>
          )}
        </div>

        {/* Collapse / Expand button — bottom of sidebar (desktop only) */}
        {!isMobile && (
          <button
            onClick={toggleSidebar}
            className={`sb-collapse-btn${collapsed ? ' centered' : ''}`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight size={16} />
            ) : (
              <>
                <ChevronLeft size={16} />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}
      </aside>

      {/* ════════ MAIN ════════ */}
      <div style={{ flex: '1 1 0%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, position: 'relative' }}>

        {/* Topbar */}
        <header style={{
          height: '60px',
          background: 'var(--bg-topbar)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center',
          padding: isMobile ? '0 12px' : '0 24px', gap: 10,
          flexShrink: 0,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          zIndex: 40,
        }}>
          {/* Hamburger — opens the drawer on mobile */}
          {isMobile && (
            <button
              onClick={() => setMobileOpen(o => !o)}
              title="Menu"
              style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <Menu size={20} />
            </button>
          )}
          <h1 style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: 'var(--text-main)', flex: 1, letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {pageTitle}
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

            {/* Dark/Light */}
            <button onClick={toggleTheme} title={isDark ? 'Light Mode' : 'Dark Mode'} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-main)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* Notifications */}
            <button onClick={() => navigate('/notifications')} title="Notifications" style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', position: 'relative' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-main)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
              <Bell size={16} />
              {unread > 0 && (
                <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px', boxSizing: 'border-box', borderRadius: 8, background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', border: '1.5px solid var(--bg-topbar)' }}>
                  {unread >= 50 ? '50+' : unread}
                </span>
              )}
            </button>

            {/* New Ticket — icon-only on mobile to save room */}
            <button onClick={() => navigate('/tickets/new')} title="New Ticket" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px' : '7px 14px', borderRadius: 8, border: 'none', background: currentTheme.primary, color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'var(--font-main)', flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = currentTheme.dark; }}
              onMouseLeave={e => { e.currentTarget.style.background = currentTheme.primary; }}>
              <Plus size={isMobile ? 16 : 14} strokeWidth={2.5} />
              {!isMobile && 'New Ticket'}
            </button>

            {/* User Dropdown */}
            <div ref={dropRef} style={{ position: 'relative' }}>
              <button onClick={() => setDropOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px 5px 6px', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
              >
                <UserAvatar name={user?.name} role={user?.role} size={28} avatarUrl={user?.avatar_url} online={online} />
                {!isMobile && <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>{user?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{user?.role}</div>
                </div>}
                {!isMobile && <ChevronDown size={14} color="var(--text-muted)" style={{ transition: 'transform 0.2s', transform: dropOpen ? 'rotate(180deg)' : 'none' }} />}
              </button>

              {dropOpen && (
                <div style={{ position: 'absolute', top: 46, right: 0, width: 220, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-md)', zIndex: 500, overflow: 'hidden', animation: 'dropIn 0.15s ease' }}>
                  {/* User info */}
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <UserAvatar name={user?.name} role={user?.role} size={38} avatarUrl={user?.avatar_url} online={online} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{user?.email}</div>
                        <span style={{ display: 'inline-block', marginTop: 5, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: 'var(--primary-light)', color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {user?.role}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Color theme in dropdown — only place */}
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Palette size={12} color="var(--text-muted)" />
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Color Theme
                        </span>
                      </div>
                      {/* Default button */}
                      <button
                        onClick={() => handleThemeChange('violet')}
                        title="Reset to default (Violet)"
                        style={{
                          fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 12,
                          border: `1.5px solid ${activeColor === 'violet' ? '#7c3aed' : 'var(--border)'}`,
                          background: activeColor === 'violet' ? '#f5f3ff' : 'transparent',
                          color: activeColor === 'violet' ? '#7c3aed' : 'var(--text-muted)',
                          cursor: 'pointer', transition: 'all 0.15s',
                          textTransform: 'uppercase', letterSpacing: '0.5px',
                          fontFamily: 'var(--font-main)',
                        }}
                      >
                        Default
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      {COLOR_THEMES.map(t => (
                        <div
                          key={t.key}
                          title={t.name}
                          onClick={() => handleThemeChange(t.key)}
                          style={{
                            width: 24, height: 24, borderRadius: '50%',
                            background: t.primary, cursor: 'pointer', flexShrink: 0,
                            transition: 'transform 0.18s, box-shadow 0.18s',
                            boxShadow: activeColor === t.key
                              ? `0 0 0 2px var(--bg-card), 0 0 0 4px ${t.primary}`
                              : '0 1px 4px rgba(0,0,0,0.2)',
                            transform: activeColor === t.key ? 'scale(1.22)' : 'scale(1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {activeColor === t.key && (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Links */}
                  {[
                    { label: 'My Profile', Icon: User, to: '/profile' },
                    ...(user?.role === 'admin' ? [{ label: 'Settings', Icon: Settings, to: '/settings' }] : []),
                    ...(user?.role !== 'user'  ? [{ label: 'Reports',  Icon: BarChart2, to: '/reports'  }] : []),
                  ].map(item => (
                    <div key={item.to} onClick={() => { setDropOpen(false); navigate(item.to); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-sub)', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <item.Icon size={14} color="var(--text-muted)" />
                      {item.label}
                    </div>
                  ))}

                  {/* Availability toggle */}
                  <div onClick={toggleOnline} title="Toggle your availability" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-sub)', borderTop: '1px solid var(--border-light)', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: online ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{online ? 'Online' : 'Away'}</span>
                    <span style={{ width: 34, height: 18, borderRadius: 10, background: online ? '#10b981' : '#cbd5e1', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                      <span style={{ position: 'absolute', top: 2, left: online ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }} />
                    </span>
                  </div>

                  {/* Dark mode */}
                  <div onClick={toggleTheme} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-sub)', borderTop: '1px solid var(--border-light)', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    {isDark ? <Sun size={14} color="var(--text-muted)" /> : <Moon size={14} color="var(--text-muted)" />}
                    {isDark ? 'Light Mode' : 'Dark Mode'}
                  </div>

                  {/* Sign out */}
                  <div onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#dc2626', borderTop: '1px solid var(--border)', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <LogOut size={14} />
                    Sign Out
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page */}
        <main style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 12px' : '22px 24px', background: 'var(--bg-body)', position: 'relative' }}>
          {/* Big logo watermark — fixed to viewport so it stays put while content scrolls */}
          <div aria-hidden style={{
            position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', userSelect: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}>
            <ItsmIcon size={640} idSuffix="-bgwm" style={{ opacity: 0.18 }} />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
